import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Newspaper, Calendar, MessageSquare, UserCog, AlertTriangle, Sparkles, CheckCheck } from 'lucide-react';
import { api } from '../lib/api';
import { useTranslation } from '../i18nContext';
import { useAuth } from '../lib/AuthContext';
import { buildLocalePath } from '../i18n';

const POLL_INTERVAL_MS = 60_000;

const ICONS = {
  news: Newspaper,
  events: Calendar,
  forum: MessageSquare,
  profile_edit: UserCog,
  dues: AlertTriangle,
};

function itemLabel(item, locale) {
  const es = locale === 'es';
  switch (item.kind) {
    case 'news':
      return es
        ? `${item.count} noticia${item.count === 1 ? '' : 's'} nueva${item.count === 1 ? '' : 's'}`
        : `${item.count} new article${item.count === 1 ? '' : 's'}`;
    case 'events':
      return es
        ? `${item.count} evento${item.count === 1 ? '' : 's'} nuevo${item.count === 1 ? '' : 's'}`
        : `${item.count} new event${item.count === 1 ? '' : 's'}`;
    case 'forum':
      return es
        ? `${item.count} tema${item.count === 1 ? '' : 's'} con actividad reciente`
        : `${item.count} thread${item.count === 1 ? '' : 's'} with new activity`;
    case 'profile_edit':
      return es
        ? `Tienes ${item.count} solicitud${item.count === 1 ? '' : 'es'} de edición pendiente${item.count === 1 ? '' : 's'}`
        : `${item.count} pending profile edit request${item.count === 1 ? '' : 's'}`;
    case 'dues': {
      const d = item.days_left ?? 0;
      if (d < 0) return es ? `Tu cuota venció hace ${Math.abs(d)} día${Math.abs(d) === 1 ? '' : 's'}` : `Your dues expired ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago`;
      if (d === 0) return es ? 'Tu cuota vence hoy' : 'Your dues expire today';
      return es ? `Tu cuota vence en ${d} día${d === 1 ? '' : 's'}` : `Your dues expire in ${d} day${d === 1 ? '' : 's'}`;
    }
    default:
      return '';
  }
}

function itemHref(item, locale) {
  switch (item.route) {
    case 'news': return buildLocalePath(locale, 'news');
    case 'events': return buildLocalePath(locale, 'events');
    case 'forum': return `/${locale}/${locale === 'es' ? 'foro' : 'forum'}`;
    case 'profile': return buildLocalePath(locale, 'profile');
    default: return buildLocalePath(locale, 'home');
  }
}

export default function NotificationBell() {
  const t = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unread_count: 0, items: [] });
  const ref = useRef(null);
  const locale = t.locale;
  const enabled = !!user && (user.role === 'interpreter' || user.role === 'admin');

  const fetchNotifications = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get('/me/notifications');
      setData(data || { unread_count: 0, items: [] });
    } catch {
      // Silent: bell stays at last known count.
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, fetchNotifications]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  async function handleItemClick(item) {
    setOpen(false);
    // Best-effort: mark domain as seen so unread counter drops on next poll.
    if (item.key) {
      try { await api.post('/me/seen', { key: item.key }); } catch { /* noop */ }
    }
    navigate(itemHref(item, locale));
    fetchNotifications();
  }

  const hasSeenBased = data.items.some((it) => it.key);
  async function markAllRead() {
    try { await api.post('/me/seen-all'); } catch { /* noop */ }
    fetchNotifications();
  }

  if (!enabled) return null;

  const count = data.unread_count || 0;
  const badge = count > 9 ? '9+' : String(count);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell-btn"
        aria-label={locale === 'es' ? 'Notificaciones' : 'Notifications'}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-md text-charcoal-600 hover:text-cmic-600 hover:bg-cmic-50 transition-colors"
      >
        <Bell size={18} />
        {count > 0 && (
          <span
            data-testid="notification-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
          >
            {badge}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notification-bell-dropdown"
          className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden z-50"
        >
          <div className="px-4 py-3 bg-gradient-to-r from-cmic-700 to-cmic-600 text-white flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">{locale === 'es' ? 'Notificaciones' : 'Notifications'}</p>
              <p className="text-[11px] text-cmic-100">
                {count === 0
                  ? (locale === 'es' ? 'Sin novedades' : 'All caught up')
                  : (locale === 'es' ? `${count} pendiente${count === 1 ? '' : 's'}` : `${count} pending`)}
              </p>
            </div>
            {hasSeenBased && (
              <button
                type="button"
                onClick={markAllRead}
                data-testid="notification-mark-all-read"
                className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-white/90 hover:text-white bg-white/15 hover:bg-white/25 rounded-lg px-2.5 py-1 transition-colors"
              >
                <CheckCheck size={13} />{locale === 'es' ? 'Marcar todas como leídas' : 'Mark all as read'}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="px-5 py-10 text-center" data-testid="notification-bell-empty">
                <Sparkles size={28} className="text-cmic-200 mx-auto mb-2" />
                <p className="text-sm text-charcoal-500">
                  {locale === 'es' ? 'No tienes notificaciones nuevas.' : 'No new notifications.'}
                </p>
              </div>
            ) : (
              data.items.map((it) => {
                const Icon = ICONS[it.kind] || Bell;
                const accent = it.kind === 'dues' ? 'bg-amber-50 text-amber-700' : 'bg-cmic-50 text-cmic-700';
                return (
                  <button
                    key={`${it.kind}-${it.route}`}
                    onClick={() => handleItemClick(it)}
                    data-testid={`notification-item-${it.kind}`}
                    className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-b-0 text-left transition-colors"
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${accent}`}>
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-charcoal-700 font-medium leading-snug">{itemLabel(it, locale)}</p>
                      <p className="text-[11px] text-cmic-600 mt-0.5">
                        {locale === 'es' ? 'Ver →' : 'View →'}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {data.items.length > 0 && (
            <Link
              to={buildLocalePath(locale, 'profile')}
              onClick={() => setOpen(false)}
              data-testid="notification-bell-footer-link"
              className="block px-4 py-2.5 text-center text-xs font-medium text-cmic-600 hover:bg-cmic-50 border-t border-gray-100"
            >
              {locale === 'es' ? 'Ir a Mi perfil' : 'Go to My profile'}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
